import { orderBy } from "lodash";
import defaultAxiosClient from "shared/service/api";
import ApiClient from "shared/service/api/ApiClient";
import {
    Client,
    DictionaryItem_Public,
    EntityNoteHeader_PublicEntityType,
    EntityNote_Public,
    EntityNote_PublicEntityType,
    IDictionaryItem_Public,
    OperationList,
} from "shared/service/api/generated";
import { INoteList } from "view360Card/models/INoteList";
import { NoteOperations } from "../enums";
import { Note, NoteAuthor, NoteDetails, NoteEditor, NoteSubject, NoteType } from "../models";
import { CreateNewNoteRequest, GetNotesParameters, UpdateNoteRequest } from "./contracts";
import { NoteSubjectTypeEnum } from "view360Card/models";

interface DictionaryCollection {
    itemList?: IDictionaryItem_Public[];
}

enum NoteEntityType {
    SALESPERSON,
    INTERMEDIARY,
    GLOBAL
}

class NotesService {
    private apiClient: Client;
    private static defaultDate = new Date(-8640000000000000);

    constructor(apiClient: Client) {
        this.apiClient = apiClient;
    }

    async getSalesPersonNotes(params: GetNotesParameters): Promise<INoteList> {
        return this.getNotesBase(NoteEntityType.SALESPERSON, params);
    }

    async getIntermediaryNotes(params: GetNotesParameters): Promise<INoteList> {
        return this.getNotesBase(NoteEntityType.INTERMEDIARY, params);
    }

    async getGlobalNotes(params: GetNotesParameters): Promise<INoteList> {
        return this.getNotesBase(NoteEntityType.GLOBAL, params);
    }

    private async getNotesBase(
        noteEntityType: NoteEntityType,
        params: GetNotesParameters
    ): Promise<INoteList> {
        const sortBy = "firstEditDate";
        const sortOrder = "desc";

        let { id, offset, limit, noteTypeCodeList, authorId, subjectCodeList, phrase } = params;

        // Convert dates to API specific local date format
        const minCreationDate =
            params.minCreationDate && this.convertDateToLocalPolishDateOnlyFormat(params.minCreationDate);
        const maxCreationDate =
            params.maxCreationDate && this.convertDateToLocalPolishDateOnlyFormat(params.maxCreationDate);
        const minModifiedDate = undefined;
        const maxModifiedDate = undefined;
        const minContactDate =
            params.minContactDate && this.convertDateToLocalPolishDateOnlyFormat(params.minContactDate);
        const maxContactDate =
            params.maxContactDate && this.convertDateToLocalPolishDateOnlyFormat(params.maxContactDate);
        const noteTypeCodeListParam =
            noteTypeCodeList && noteTypeCodeList.length > 0 ? noteTypeCodeList.join(",") : undefined;
        const subjectCodeListParam =
            subjectCodeList && subjectCodeList.length > 0 ? subjectCodeList.join(",") : undefined;
        const phraseParam =
            phrase && phrase.length > 0 ? phrase : undefined;

        let response;
        switch (noteEntityType) {
            case NoteEntityType.SALESPERSON:
                response = await this.apiClient.v360_salesperson_notes_getList(
                    id,
                    offset.toString(),
                    limit.toString(),
                    undefined,
                    sortBy,
                    sortOrder,
                    minCreationDate,
                    maxCreationDate,
                    minModifiedDate,
                    maxModifiedDate,
                    minContactDate,
                    maxContactDate,
                    undefined,
                    noteTypeCodeListParam,
                    authorId,
                    undefined,
                    subjectCodeListParam,
                    undefined,
                );
            break;
            case NoteEntityType.INTERMEDIARY:
                response = await this.apiClient.v360_intermediary_notes_getList(
                    id,
                    offset.toString(),
                    limit.toString(),
                    undefined,
                    sortBy,
                    sortOrder,
                    minCreationDate,
                    maxCreationDate,
                    minModifiedDate,
                    maxModifiedDate,
                    minContactDate,
                    maxContactDate,
                    undefined,
                    noteTypeCodeListParam,
                    authorId,
                    undefined,
                    subjectCodeListParam,
                    undefined
                );
                break;
            case NoteEntityType.GLOBAL:
            default:
                response = await this.apiClient.note_search(
                    offset.toString(),
                    limit.toString(),
                    phraseParam,
                    sortBy,
                    sortOrder,
                    minCreationDate,
                    maxCreationDate,
                    minModifiedDate,
                    maxModifiedDate,
                    minContactDate,
                    maxContactDate,
                    undefined,
                    noteTypeCodeListParam,
                    authorId,
                    undefined,
                    subjectCodeListParam,
                    undefined
                );
                break;
        }

        const notes =
            response.list?.map(
                (noteDto) =>
                    new Note(
                        noteDto.id ?? 0,
                        noteDto.entityType ?? EntityNoteHeader_PublicEntityType.INTERMEDIARY,
                        noteDto.entityId ?? 0,
                        noteDto.noteType?.description ?? "",
                        noteDto.contactDate
                            ? new Date(noteDto.contactDate)
                            : NotesService.defaultDate,
                        new NoteEditor(
                            noteDto.author?.fullName ?? "",
                            noteDto.author?.mobilePhone,
                            noteDto.author?.userPrincipalName
                        )
                    )
            ) ?? [];

        return { notes: notes, hasMore: response.hasMore ?? false };
    }

    async getAvailableNoteTypes() {
        const response = await defaultAxiosClient.get<DictionaryCollection[]>("/secured/dictionary?code=NOTE_TYPE");
        if (response.data.length === 0 || !response.data[0].itemList) {
            return [];
        }

        const models =
            orderBy(response.data[0].itemList, (x) => x.orderIndex ?? Number.MAX_VALUE, "asc").map(
                (item) => new NoteType(item.code, item.description ?? "")
            ) ?? [];

        return models;
    }

    async getAvailableNoteSubjects() {
        const response = await defaultAxiosClient.get<DictionaryCollection[]>("/secured/dictionary?code=NOTE_SUBJECT");
        if (response.data.length === 0 || !response.data[0].itemList) {
            return [];
        }
        const models =
            orderBy(response.data[0].itemList, (x) => x.orderIndex ?? Number.MAX_VALUE, "asc").filter(x => x.flexField1 === NoteSubjectTypeEnum.RETAIL).map(
                (item) => new NoteSubject(item.code, item.description ?? "")
            ) ?? [];

        return models;
    }

    async getAvailableNoteAuthors() {
        const response = await this.apiClient.note_users();

        const models = response.map((item) => new NoteAuthor(
            item.id?.toString() ?? "",
            item.firstName ?? "",
            item.lastName ?? ""
        ))

        return models;
    }

    async getIntermediaryNoteDetails(intermediaryId: number, noteId: number): Promise<NoteDetails> {
        const response = await this.apiClient.v360_intermediary_notes_getOne(intermediaryId, noteId);
        const model = this.mapNoteDetails(response);

        return model;
    }

    async getSalesPersonNoteDetails(salesPersonId: number, noteId: number): Promise<NoteDetails> {
        const response = await this.apiClient.v360_salesperson_notes_getOne(salesPersonId, noteId);
        const model = this.mapNoteDetails(response);

        return model;
    }

    private mapNoteDetails(noteDto: EntityNote_Public) {
        const model = new NoteDetails(
            noteDto.id ?? 0,
            noteDto.firstEditDate ? new Date(noteDto.firstEditDate) : NotesService.defaultDate,
            new NoteEditor(
                noteDto.author?.fullName ?? "",
                noteDto.author?.mobilePhone,
                noteDto.author?.userPrincipalName
            ),
            noteDto.lastEditDate ? new Date(noteDto.lastEditDate) : NotesService.defaultDate,
            new NoteEditor(
                noteDto.lastEditBy?.fullName ?? "",
                noteDto.lastEditBy?.mobilePhone,
                noteDto.lastEditBy?.userPrincipalName
            ),
            noteDto.entityName ?? "",
            noteDto.entityType ?? EntityNote_PublicEntityType.INTERMEDIARY,
            noteDto.entityId ?? 0,
            noteDto.intermediaryReferenceList ?? [],
            new NoteType(noteDto.noteType?.code ?? "", noteDto.noteType?.description ?? ""),
            noteDto.contactDate ? new Date(noteDto.contactDate) : NotesService.defaultDate,
            noteDto.subjectRetailList?.map((subject) => new NoteSubject(subject.code ?? "", subject.description ?? "")) ?? [],
            noteDto.subjectCorpoList?.map((subject) => new NoteSubject(subject.code ?? "", subject.description ?? "")) ?? [],
            noteDto.body ?? "",
            noteDto.operationList
                ?.filter((operation) => operation === OperationList.EDIT || operation === OperationList.REMOVE)
                .map((operation) => {
                    switch (operation) {
                        case OperationList.EDIT:
                            return NoteOperations.edit;
                        case OperationList.REMOVE:
                            return NoteOperations.remove;
                    }
                }) ?? []
        );

        return model;
    }

    async addSalesPersonNote(salesPersonId: number, request: CreateNewNoteRequest) {
        const response = await this.apiClient.v360_salesperson_notes_create(
            salesPersonId,
            this.mapCreateNoteRequest(request)
        );
        return response.id;
    }

    async addIntermediaryNote(intermediaryId: number, request: CreateNewNoteRequest) {
        const response = await this.apiClient.v360_intermediary_notes_create(
            intermediaryId,
            this.mapCreateNoteRequest(request)
        );
        return response.id;
    }

    private mapCreateNoteRequest(request: CreateNewNoteRequest) {
        return new EntityNote_Public({
            noteType: new DictionaryItem_Public({ code: request.noteTypeCode }),
            contactDate: this.convertDateToLocalPolishDate(request.contactDate),
            subjectRetailList: request.subjectRetailCodes.map((code) => new DictionaryItem_Public({ code: code })),
            subjectCorpoList: request.subjectCorpoCodes.map((code) => new DictionaryItem_Public({ code: code })),
            body: request.body,
            intermediaryReferenceList: request.intermediaryReferenceList
        });
    }

    async updateSalesPersonNote(salesPersonId: number, noteId: number, request: UpdateNoteRequest) {
        const response = await this.apiClient.v360_salesperson_notes_modify(
            salesPersonId,
            noteId,
            this.mapUpdateNoteRequest(request)
        );
        return response.id;
    }

    async updateIntermediaryNote(intermediaryId: number, noteId: number, request: UpdateNoteRequest) {
        const response = await this.apiClient.v360_intermediary_notes_modify(
            intermediaryId,
            noteId,
            this.mapUpdateNoteRequest(request)
        );
        return response.id;
    }

    private mapUpdateNoteRequest(request: UpdateNoteRequest) {
        return new EntityNote_Public({
            noteType: new DictionaryItem_Public({ code: request.noteTypeCode }),
            contactDate: this.convertDateToLocalPolishDate(request.contactDate),
            subjectRetailList: request.subjectRetailCodes.map((code) => new DictionaryItem_Public({ code: code })),
            subjectCorpoList: request.subjectCorpoCodes.map((code) => new DictionaryItem_Public({ code: code })),
            body: request.body,
            intermediaryReferenceList: request.intermediaryReferenceList
        });
    }

    async deleteSalesPersonNote(salesPersonId: number, noteId: number) {
        await this.apiClient.v360_salesperson_notes_delete(salesPersonId, noteId);
    }

    async deleteIntermediaryNote(intermediaryId: number, noteId: number) {
        await this.apiClient.v360_intermediary_notes_delete(intermediaryId, noteId);
    }

    private convertDateToLocalPolishDateOnlyFormat(date: Date) {
        const polishDate = new Date(date.toLocaleDateString("en", { timeZone: "Europe/Warsaw" }));

        const polishLanguageCode = "pl";
        const year = new Intl.DateTimeFormat(polishLanguageCode, { year: "numeric" }).format(polishDate);
        const month = new Intl.DateTimeFormat(polishLanguageCode, { month: "2-digit" }).format(polishDate);
        const day = new Intl.DateTimeFormat(polishLanguageCode, { day: "2-digit" }).format(polishDate);

        return `${year}-${month}-${day}`;
    }

    private convertDateToLocalPolishDate(date: Date) {
        return new Date(date.toLocaleDateString("en", { timeZone: "Europe/Warsaw" }));
    }
}

export default new NotesService(ApiClient);
